import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

export function initSentry() {
  // Treat placeholder values the same as missing. DO App Platform
  // ships specs with `REPLACE` for vars that should be filled in
  // before deploy; if one slips through, init with the bogus DSN
  // silently no-ops, so we'd lose all client errors. Bail loudly.
  if (!SENTRY_DSN || SENTRY_DSN === "REPLACE") return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: `lilia-editor@${import.meta.env.VITE_APP_VERSION || "dev"}`,
    // No session replay (16 Oct): unmasked, it recorded the text of people's documents, and our
    // error tracker (self-hosted Bugsink) doesn't keep replays anyway.
    // No session pings either: Bugsink ignores them, and they were one request per page load.
    integrations: (defaults) => defaults.filter((i) => i.name !== "BrowserSession"),
    tracesSampleRate: 0, // Exceptions only — no performance tracing
    beforeSend(event, hint) {
      // Strip PII — don't send user emails in error reports
      if (event.user) {
        delete event.user.email;
        delete event.user.ip_address;
      }

      // Drop stale-chunk errors that lazyWithRetry already recovers
      // from via window.location.reload(). The throw inside lazy()
      // surfaces to React's error boundary and Sentry, but the user
      // is already on their way to the new build by then. Leaving
      // these events in the dashboard makes every deploy look like a
      // 28-user incident when it's really a transparent self-heal.
      const message =
        (hint?.originalException as Error | undefined)?.message ??
        event.exception?.values?.[0]?.value ??
        "";
      if (
        /Failed to fetch dynamically imported module/i.test(message) ||
        /Importing a module script failed/i.test(message) ||
        /'text\/html' is not a valid JavaScript MIME type/i.test(message) ||
        /Unable to preload CSS for/i.test(message)
      ) {
        return null;
      }

      return event;
    },
  });
}

export { Sentry };
